/**
 * EIP-1193 / JSON-RPC error normalisation shared by every wallet flow
 * (connect, sign, send, call, deploy). Turns provider noise into one
 * predictable shape the tx modals can render.
 */

export type WalletErrorKind =
  | "no-provider"
  | "rejected"
  | "pending-request"
  | "unauthorized"
  | "disconnected"
  | "wrong-chain"
  | "unsupported-chain"
  | "insufficient-funds"
  | "gas-estimate"
  | "reverted"
  | "nonce"
  | "underpriced"
  | "rate-limited"
  | "timeout"
  | "unknown";

export type NormalizedWalletError = {
  kind: WalletErrorKind;
  code: number | null;
  title: string;
  message: string;
  /** Safe to offer a retry button. */
  retryable: boolean;
  /** Original message, for the details disclosure. */
  raw: string;
};

function pickCode(err: unknown): number | null {
  const e = err as { code?: unknown; data?: { code?: unknown }; cause?: { code?: unknown } } | null;
  const candidates = [e?.code, e?.data?.code, e?.cause?.code];
  for (const c of candidates) {
    if (typeof c === "number") return c;
    if (typeof c === "string" && /^-?\d+$/.test(c)) return Number(c);
  }
  return null;
}

function pickMessage(err: unknown): string {
  if (typeof err === "string") return err;
  const e = err as
    | { message?: unknown; shortMessage?: unknown; data?: { message?: unknown }; details?: unknown }
    | null;
  for (const v of [e?.shortMessage, e?.message, e?.data?.message, e?.details]) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  try {
    return JSON.stringify(err);
  } catch {
    return String(err);
  }
}

export function normalizeWalletError(err: unknown): NormalizedWalletError {
  const raw = pickMessage(err);
  const code = pickCode(err);
  const low = raw.toLowerCase();

  const out = (
    kind: WalletErrorKind,
    title: string,
    message: string,
    retryable = true,
  ): NormalizedWalletError => ({ kind, code, title, message, retryable, raw });

  if (code === 4001 || /user (rejected|denied|cancel)|rejected the request|request rejected/.test(low)) {
    return out("rejected", "Request cancelled", "You dismissed the wallet prompt. Nothing was sent.");
  }
  if (code === -32002 || /already pending|request of type/.test(low)) {
    return out(
      "pending-request",
      "Wallet is already asking",
      "Open your wallet and finish or dismiss the prompt that is already waiting, then try again.",
    );
  }
  if (code === 4100 || /unauthorized|not been authorized/.test(low)) {
    return out("unauthorized", "Wallet not connected", "Connect the wallet and approve this site first.");
  }
  if (code === 4900 || code === 4901 || /disconnected|provider is not connected/.test(low)) {
    return out("disconnected", "Wallet disconnected", "Your wallet lost its network connection. Reconnect and retry.");
  }
  if (code === 4902 || /unrecognized chain|chain .* not added|unsupported chain/.test(low)) {
    return out("unsupported-chain", "Network missing", "Add this network in your wallet, then retry.");
  }
  if (/wrong network|chain mismatch|switch to/.test(low)) {
    return out("wrong-chain", "Wrong network", raw);
  }
  if (/insufficient funds|insufficient balance|exceeds .*balance/.test(low)) {
    return out(
      "insufficient-funds",
      "Not enough funds",
      "This wallet cannot cover the amount plus network fees. Top it up and retry.",
      false,
    );
  }
  if (/gas required exceeds|cannot estimate gas|gas estimation|intrinsic gas too low/.test(low)) {
    return out(
      "gas-estimate",
      "Fee estimate failed",
      "The network could not price this transaction — it would likely fail. Check the inputs and retry.",
    );
  }
  if (/execution reverted|revert|call exception/.test(low)) {
    return out("reverted", "Transaction would fail", raw, false);
  }
  if (/nonce too low|already known|replacement transaction underpriced/.test(low)) {
    return out(
      "nonce",
      "Transaction clash",
      "An earlier transaction from this wallet is still in flight. Wait for it to settle, then retry.",
    );
  }
  if (/underpriced|fee too low|max fee per gas/.test(low)) {
    return out("underpriced", "Fee too low", "Raise the network fee in your wallet and retry.");
  }
  if (code === -32005 || /rate limit|too many requests|429/.test(low)) {
    return out("rate-limited", "Network busy", "The node is rate-limiting requests. Wait a moment and retry.");
  }
  if (/timeout|timed out/.test(low)) {
    return out("timeout", "Timed out", "The network did not answer in time. Retry when you are ready.");
  }
  if (/no (eip-1193 |injected )?wallet|no provider|ethereum is not defined/.test(low)) {
    return out(
      "no-provider",
      "No wallet found",
      "Install MetaMask or Rainbow, or open this page inside your wallet's browser.",
      false,
    );
  }
  return out("unknown", "Wallet request failed", raw);
}

export function walletErrorMessage(err: unknown): string {
  const n = normalizeWalletError(err);
  return n.kind === "unknown" ? n.message : `${n.title} — ${n.message}`;
}

export const NO_WALLET_ERROR = new Error(
  "No EIP-1193 wallet found. Install MetaMask or Rainbow, or open this page in your wallet's browser.",
);
